import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Card, Col, Row, Space } from 'antd';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import TableBase from 'common/components/table/table-base';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import { ColumnVisibility } from 'common/components/table/filters/column-visibility/column-visibility';
import EiqTabs from 'common/components/tabs/eiq-tabs';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { RentalPropertyStatusEnum } from 'common/enums/rental-property-status.enum';
import rentalAgencyChangeService from 'common/services/rental-agency-change-service/rental-agency-change.service';
import rentalPropertyService from 'common/services/rental-property-service/rental-property.service';
import {
  filterColumnsByPersonalization,
  getTablePersonalizationColumnsData,
  getTablePersonalizationSortData,
  personalizationDefaultTaleSort,
} from 'common/components/table/table-helpers';
import userPersonalizationStore from 'common/stores/user-personalization/user-personalization.store';
import styles from '../../rental-portal.module.scss';
import { rentalPropertiesInitialColumns } from './rental-properties.config';
import { IRentalApplicationRow, toRentalApplicationRows } from './rental-properties.model';

const TABLE_NAME = 'rental-properties';
const ALL_TAB_KEY = 'All';
const DEFAULT_SORT = { key: 'createdAt', order: 'desc' };

export const RentalProperties = observer(() => {
  const [applications, setApplications] = useState<IRentalApplicationRow[]>([]);

  const tableControls = useTableControls({
    clientSide: true,
    data: applications,
    initialColumns: filterColumnsByPersonalization(rentalPropertiesInitialColumns, TABLE_NAME),
    defaultSort: personalizationDefaultTaleSort(TABLE_NAME) ?? DEFAULT_SORT,
    onUpdateColumns: async (columns: any[], sortBy: any) => {
      await userPersonalizationStore.updateTableColumnsPersonalization({
        tableName: TABLE_NAME,
        tableColumns: getTablePersonalizationColumnsData(columns),
        tableSort: getTablePersonalizationSortData(sortBy),
      });
    },
  });

  useEffect(() => {
    Promise.all([rentalPropertyService.getRentalProperties(), rentalAgencyChangeService.getRentalAgencyChanges()]).then(
      ([properties, agencyChanges]) => setApplications(toRentalApplicationRows(properties, agencyChanges)),
    );
  }, []);

  const countByStatus = (status: RentalPropertyStatusEnum) => applications.filter((application) => application.status === status).length;

  const tabItems = [
    { key: ALL_TAB_KEY, label: `All (${applications.length})` },
    { key: RentalPropertyStatusEnum.Pending, label: `Pending (${countByStatus(RentalPropertyStatusEnum.Pending)})` },
    { key: RentalPropertyStatusEnum.Approved, label: `Approved (${countByStatus(RentalPropertyStatusEnum.Approved)})` },
    { key: RentalPropertyStatusEnum.Rejected, label: `Rejected (${countByStatus(RentalPropertyStatusEnum.Rejected)})` },
  ];

  const handleTabChange = (key: string) => {
    if (key === ALL_TAB_KEY) {
      tableControls.resetFilter('AND');
    } else {
      tableControls.setFilter('status', { operator: FilterTypesEnum.Equals, value: key }, 'AND');
    }
  };

  return (
    <TableContextProvider controls={tableControls}>
      <Card className="eiq-card eiq-card-border full-card">
        <Row className="eiq-card-header">
          <Col span={24}>
            <h1 className="h1">Rental Properties</h1>
          </Col>
        </Row>
        <EiqTabs className={styles.statusTabs} defaultActiveKey={ALL_TAB_KEY} items={tabItems} onChange={handleTabChange} />
        <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
          <Col flex="auto">
            <SearchFilter
              placeholder="Search by address, owner, or agency"
              rulesForColumn={{
                address: FilterTypesEnum.Contains,
                ownerName: FilterTypesEnum.Contains,
                managingAgencyName: FilterTypesEnum.Contains,
              }}
            />
          </Col>
          <Col>
            <Space size={8}>
              <ColumnVisibility isColumnReorderAllow={true} />
            </Space>
          </Col>
        </Row>
        <TableBase onSelected={tableControls.setSelectedRow} selectFirstRowByDefault />
      </Card>
    </TableContextProvider>
  );
});
